import { useState } from "react";
import { Info, Loader2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { DataStatusChip } from "@/components/common/DataStatusChip";

/**
 * ProvenanceButton — small [ⓘ] icon that opens a slide-over showing the full
 * GBE Hub Enterprise Data Dictionary row for a given Enterprise ID (e.g.
 * "CRM-KPI-004"). Fetches lazily on first open.
 *
 * <ProvenanceButton enterpriseId="CRM-KPI-004" />
 * <ProvenanceButton enterpriseId="CRM-KPI-004" size="sm" />
 */

const CLASS_TO_CHIP = {
  "Source Data": "source",
  "Hub Calculated": "calculated",
  "AI Generated": "ai_generated",
  "Manager Adjusted": "manager_adjusted",
  "AI Extracted – Pending Verification": "ai_pending",
};

export function ProvenanceButton({ enterpriseId, size = "sm", className = "" }) {
  const [open, setOpen] = useState(false);
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const iconSize = size === "xs" ? "w-3 h-3" : "w-3.5 h-3.5";

  const load = async () => {
    if (row || loading) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/data-mapping/by-id/${encodeURIComponent(enterpriseId)}`);
      setRow(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Not found");
    } finally { setLoading(false); }
  };

  if (!enterpriseId) return null;

  return (
    <>
      <button
        type="button"
        data-testid={`provenance-${enterpriseId}`}
        onClick={(e) => { e.stopPropagation(); setOpen(true); load(); }}
        title={`View data provenance (${enterpriseId})`}
        className={`inline-flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors p-1 ${className}`}
      >
        <Info className={iconSize} />
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto p-0">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-200">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
                {enterpriseId}
              </span>
              {row?.data_classification && (
                <DataStatusChip
                  status={CLASS_TO_CHIP[row.data_classification] || "source"}
                  size="xs"
                />
              )}
            </div>
            <SheetTitle className="font-display text-lg">
              {loading ? "Loading…" : (row?.kpi || error || enterpriseId)}
            </SheetTitle>
            <SheetDescription className="sr-only">
              GBE Hub Enterprise Data Dictionary provenance record for {enterpriseId}
            </SheetDescription>
            {row?.data_point && (
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">{row.data_point}</p>
            )}
          </SheetHeader>

          <div className="px-6 py-5 space-y-4 text-sm">
            {loading && (
              <div className="flex items-center gap-2 text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Fetching dictionary…
              </div>
            )}
            {error && !loading && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                Could not load provenance: {error}. Ensure the Enterprise Data Dictionary has been imported at
                <a href="/admin/data-governance" className="underline ml-1">Data Governance</a>.
              </div>
            )}
            {row && !loading && (
              <>
                <Section title="Module">
                  <div className="font-medium text-slate-900">{row.module}</div>
                  {row.tab && <div className="text-xs text-slate-500">{row.tab}</div>}
                </Section>

                <div className="grid grid-cols-2 gap-3">
                  <MetaField label="Source System" value={row.source_system} />
                  <MetaField label="System of Record" value={row.system_of_record} />
                  <MetaField label="API / Integration" value={row.api_integration} mono />
                  <MetaField label="Source Field" value={row.source_field} mono />
                  <MetaField label="Update Frequency" value={row.update_frequency} />
                  <MetaField label="Data Type" value={row.data_type} />
                  <MetaField label="Priority" value={row.priority} />
                  <MetaField label="Mandatory" value={row.mandatory_raw || (row.mandatory ? "Yes" : "No")} />
                  <MetaField label="BU Filter" value={row.bu_filter_raw || (row.bu_filter ? "Yes" : "No")} />
                  <MetaField label="Client Visible" value={row.client_visible_raw || (row.client_visible ? "Yes" : "No")} />
                  <MetaField label="Write-Back" value={row.write_back} />
                  <MetaField label="Permission" value={row.permission} />
                </div>

                {row.calculation && (
                  <Section title="Calculation">
                    <code className="text-xs block bg-slate-50 border border-slate-200 rounded p-2 whitespace-pre-wrap font-mono text-slate-700">
                      {row.calculation}
                    </code>
                  </Section>
                )}

                {row.ai_agent && (
                  <Section title="AI Agent">
                    <div className="rounded-md border border-violet-200 bg-violet-50/70 px-3 py-2 text-xs text-violet-900">
                      {row.ai_agent}
                    </div>
                  </Section>
                )}

                {row.dashboard_widget && (
                  <MetaField label="Widget" value={row.dashboard_widget} />
                )}

                {row.notes && (
                  <Section title="Notes">
                    <p className="text-xs text-slate-600 leading-relaxed">{row.notes}</p>
                  </Section>
                )}

                <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
                  <span>Imported {(row.imported_at || "").slice(0, 10)} · by {row.imported_by}</span>
                  <a href="/admin/data-governance" className="inline-flex items-center gap-1 hover:text-slate-800">
                    Full dictionary <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{title}</div>
      {children}
    </div>
  );
}
function MetaField({ label, value, mono = false }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-slate-800 mt-0.5 ${mono ? "font-mono text-[11px] break-all" : "text-sm"}`}>
        {value || <span className="text-slate-400">—</span>}
      </div>
    </div>
  );
}
